import { useAppDispatch, useAppSelector } from "../../app";
import { annotationsForEntitySelector } from "../index";
import { useCallback, useMemo } from "react";
import {
  addAnnotationToState,
  removeAllAnnotationsForDocument,
  removeAnnotationFromState,
  removeAnnotationsForEntitiesByGroupBlockIndex,
  removeAnnotationsForEntity,
  updateAnnotationInState,
  updateGroupBlockAnnotations,
  shiftAnnotation,
  updateAnnotationsBatch,
} from "../store/annotationSlice";
import { changeHasBeenEdited } from "../../app/store/appSlice";
import { Annotation } from "../../annotator/interfaces/annotation";
import { GroupBlockEntityType } from "../../configMap";
import { getAnnotationForId } from "../utils/utils";
import { incrementOperations } from "../../analytics/store/analyticsSlice";
import { UpdateMultiGroupBlockValuePayload } from "../interfaces/annotation";
import { throttle } from "../../common/utilities/func";
import { Status } from "../../common/status/status";
import useAnnotationNormalization from "../../configMap/hooks/useAnnotationNormalization";
import { findOriginalIndex } from "../../documentTypes/utils/synchronizeAnnotationHelper";

type Props = {
  documentId?: string;
  entityId?: string;
  groupBlockIndex?: number;
};

const useAnnotations = ({ documentId, entityId, groupBlockIndex }: Props) => {
  const dispatch = useAppDispatch();
  // TODO Do we need it? Why is this done or used in the useeffect deps?
  // const { setInitialMultipleGroupBlocksCount } = useMultipleGroupBlocks();

  const { normalizeIfAnnotationNeedsToBeNormalized } =
    useAnnotationNormalization();

  const { activeDocumentSet } = useAppSelector(
    (state) => state.documentSetsReducer,
  );

  const {
    tableControlAnnotations,
    sideControlAnnotations,
    loadingAnnotationIds,
  } = useAppSelector((state) => state.annotationReducer);

  const categorizationAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState.categorizationAnnotations,
  );

  const annotationsForEntity = useAppSelector((state) =>
    annotationsForEntitySelector(state, {
      documentId,
      entityId,
      groupBlockIndex,
    }),
  );

  const annotations = useMemo<Array<Annotation>>(() => {
    if (!tableControlAnnotations && sideControlAnnotations) {
      return [];
    }

    return tableControlAnnotations.concat(sideControlAnnotations);
  }, [tableControlAnnotations, sideControlAnnotations]);

  const annotationsForAnnotator = useMemo((): Array<Annotation> => {
    if (activeDocumentSet?.status === Status.Error) {
      return [];
    }

    if (!documentId) {
      return [];
    }

    return annotations.filter(
      (annotation) => annotation.id && annotation.documentId === documentId,
    );
  }, [annotations, activeDocumentSet, documentId]);

  const removeAllAnnotations = useCallback(() => {
    if (!documentId) {
      return;
    }

    dispatch(
      incrementOperations({
        numberOfAdditions: 0,
        numberOfDeletions: annotations.length,
        numberOfUpdates: 0,
      }),
    );
    dispatch(removeAllAnnotationsForDocument(documentId.toString()));
    dispatch(changeHasBeenEdited(true));
  }, [documentId, dispatch, annotations]);

  const removeAnnotation = useCallback(
    (
      annotationId: string,
      multipleGroupBlocks: boolean,
      userAction = false,
    ) => {
      if (!documentId) {
        return;
      }

      dispatch(
        removeAnnotationFromState({
          documentId,
          annotationId,
          multipleGroupBlocks,
        }),
      );
      dispatch(changeHasBeenEdited(true));

      if (userAction) {
        dispatch(
          incrementOperations({
            numberOfAdditions: 0,
            numberOfDeletions: 1,
            numberOfUpdates: 0,
          }),
        );
      }
    },
    [dispatch, documentId],
  );

  const updateAnnotation = useCallback(
    (
      annotationId: string,
      newValue: string,
      entity: GroupBlockEntityType,
      userAction: boolean,
      multipleGroupBlocks: boolean,
    ) => {
      if (!documentId || !groupBlockIndex) {
        return;
      }

      const annotation = getAnnotationForId(
        annotationId,
        groupBlockIndex,
        annotations,
        entity,
      );

      if (!annotation) {
        return;
      }

      dispatch(changeHasBeenEdited(true));

      if (!newValue) {
        removeAnnotation(annotationId, multipleGroupBlocks);

        if (userAction) {
          dispatch(
            incrementOperations({
              numberOfAdditions: 0,
              numberOfDeletions: 1,
              numberOfUpdates: 0,
            }),
          );
        }
        return;
      }

      const hasTriggerAfterManualUpdate =
        annotation?.entity.entityNormalizations?.some(
          (item) => item.normalizationStrategy?.triggerAfterManualUpdate,
        ) || false;

      if (!annotationId) {
        const originalIndex = findOriginalIndex(
          annotations,
          categorizationAnnotations || [],
          entity.groupBlockId,
          annotation.index,
        );

        dispatch(
          addAnnotationToState({
            documentId,
            annotation: {
              ...annotation,
              documentId: documentId,
              isByUser: true,
              values: [],
              pageTokenIndices: [],
              entityAnnotationNormalization: {
                normalizedMethod: "manual",
                normalizedValue: newValue,
                options: annotation.entityAnnotationNormalization?.options,
                isByUser: true,
              },
              multipleGroupBlocks,
              originalIndex,
              groupBlockId: entity.groupBlockId,
            },
            multipleGroupBlocks,
          }),
        );

        if (userAction) {
          dispatch(
            incrementOperations({
              numberOfAdditions: 1,
              numberOfDeletions: 0,
              numberOfUpdates: 0,
            }),
          );
        }

        if (hasTriggerAfterManualUpdate) {
          normalizeIfAnnotationNeedsToBeNormalized([
            {
              ...annotation,
              multipleGroupBlocks,
              entityAnnotationNormalization: {
                normalizedMethod: "manual",
                normalizedValue: newValue,
                options: annotation.entityAnnotationNormalization?.options,
                isByUser: true,
              },
              triggerNormalization: hasTriggerAfterManualUpdate,
            },
          ]);
        }
        return;
      }

      if (hasTriggerAfterManualUpdate) {
        normalizeIfAnnotationNeedsToBeNormalized([
          {
            ...annotation,
            multipleGroupBlocks,
            entityAnnotationNormalization: {
              normalizedMethod: "manual",
              normalizedValue: newValue,
              options: annotation.entityAnnotationNormalization?.options,
              isByUser: true,
            },
            triggerNormalization: hasTriggerAfterManualUpdate,
          },
        ]);
      } else {
        dispatch(
          updateAnnotationInState({
            annotation: {
              ...annotation,
              isByUser: true,
            },
            newValue: newValue,
            method: "manual",
            options: annotation.entityAnnotationNormalization?.options,
            multipleGroupBlocks,
            isByUser: true,
          }),
        );
      }

      if (userAction) {
        dispatch(
          incrementOperations({
            numberOfAdditions: 0,
            numberOfDeletions: 0,
            numberOfUpdates: 1,
          }),
        );
      }
    },
    [
      documentId,
      groupBlockIndex,
      annotations,
      dispatch,
      removeAnnotation,
      categorizationAnnotations,
      normalizeIfAnnotationNeedsToBeNormalized,
    ],
  );

  const updateAnnotations = useCallback(
    (newAnnotations: Array<Annotation>, multipleGroupBlocks: boolean) => {
      if (!documentId) {
        return;
      }

      const filteredExistingAnnotations = annotations.filter(
        (a) => a.multipleGroupBlocks === multipleGroupBlocks,
      );

      dispatch(changeHasBeenEdited(true));
      dispatch(
        updateAnnotationsBatch({
          documentId,
          annotations: newAnnotations,
          multipleGroupBlocks,
        }),
      );

      normalizeIfAnnotationNeedsToBeNormalized(newAnnotations);

      const diff = filteredExistingAnnotations.length - newAnnotations.length;

      if (diff === 0) {
        dispatch(
          incrementOperations({
            numberOfAdditions: 0,
            numberOfDeletions: 0,
            numberOfUpdates: 1,
          }),
        );
      } else if (diff <= -1) {
        dispatch(
          incrementOperations({
            numberOfAdditions: Math.abs(diff),
            numberOfDeletions: 0,
            numberOfUpdates: 0,
          }),
        );
      } else if (diff >= 1) {
        dispatch(
          incrementOperations({
            numberOfAdditions: 0,
            numberOfDeletions: diff,
            numberOfUpdates: 0,
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      documentId,
      annotations,
      normalizeIfAnnotationNeedsToBeNormalized,
      dispatch,
    ],
  );

  const clearAnnotationsForEntities = useCallback(
    (
      entityIds: Array<string>,
      groupBlockIndex: number,
      isGroupRemoved: boolean,
      multipleGroupBlocks: boolean,
    ) => {
      if (!documentId) {
        return [];
      }

      const annotationsRemoved = annotations.filter(
        (annotation) =>
          entityIds.includes(annotation.entity.id) &&
          annotation.index === groupBlockIndex,
      ).length;

      dispatch(
        incrementOperations({
          numberOfAdditions: 0,
          numberOfUpdates: 0,
          numberOfDeletions: annotationsRemoved,
        }),
      );
      dispatch(
        removeAnnotationsForEntitiesByGroupBlockIndex({
          documentId,
          groupBlockIndex,
          entityIds,
          isGroupRemoved,
          multipleGroupBlocks,
        }),
      );
      dispatch(changeHasBeenEdited(true));
    },
    [documentId, annotations, dispatch],
  );

  // `useMemo` rather than `useCallback` so the `throttle()` call is the memoized
  // expression instead of an argument re-evaluated (and discarded) every render.
  // The returned instance is identical to what `useCallback` produced.
  const updateMultiGroupBlockAnnotations = useMemo(
    () =>
      throttle(
        (
          multipleGroupBlocks: boolean,
          entityIds: Array<string>,
          groupBlockIndex: number,
          documentId: string,
          addBefore: boolean = false,
        ) => {
          const payload: UpdateMultiGroupBlockValuePayload = {
            entityIds,
            documentId,
            groupBlockIndex,
            addBefore,
            multipleGroupBlocks,
          };
          if (documentId) {
            dispatch(updateGroupBlockAnnotations(payload));
          }
        },
        250,
      ),
    [dispatch],
  );

  const clearAnnotationsForEntity = useCallback(
    (
      entityIds: Array<string>,
      groupBlockIndex: number,
      multipleGroupBlocks: boolean,
    ) => {
      if (!documentId) {
        return [];
      }

      dispatch(
        incrementOperations({
          numberOfAdditions: 0,
          numberOfUpdates: 0,
          numberOfDeletions: 1,
        }),
      );
      dispatch(
        removeAnnotationsForEntity({
          documentId,
          groupBlockIndex,
          entityIds,
          multipleGroupBlocks,
        }),
      );
      dispatch(changeHasBeenEdited(true));
    },
    [documentId, dispatch],
  );

  const isAnnotationLoading = useMemo(() => {
    return (
      annotationsForEntity?.length === 1 &&
      loadingAnnotationIds.includes(annotationsForEntity[0].id)
    );
  }, [annotationsForEntity, loadingAnnotationIds]);

  const shiftAnnotationHandler = useCallback(
    (entityTypeId: string, index: number, direction: 1 | -1) => {
      if (!documentId) {
        return;
      }

      dispatch(
        shiftAnnotation({
          entityTypeId,
          documentId,
          index,
          direction,
        }),
      );
      dispatch(changeHasBeenEdited(true));
    },
    [documentId, dispatch],
  );

  return {
    annotationsForEntity,
    annotationsForAnnotator,
    annotations,
    updateAnnotation,
    updateAnnotations,
    removeAnnotation,
    removeAllAnnotations,
    clearAnnotationsForEntities,
    updateMultiGroupBlockAnnotations,
    clearAnnotationsForEntity,
    isAnnotationLoading,
    shiftAnnotationHandler,
  };
};

export default useAnnotations;
